import React from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Switch, Tooltip, CircularProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon, VisibilityOutlined as ViewIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import { NotificationItem } from '../types';

interface NotificationTableProps {
  notifications: NotificationItem[];
  loading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onToggleActive: (id: number, currentStatus: boolean) => void;
  onDelete: (id: number) => void;
  onOpenStats: (id: number) => void;
}

const NotificationTable: React.FC<NotificationTableProps> = ({
  notifications,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onToggleActive,
  onDelete,
  onOpenStats
}) => {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        borderColor: '#e2e8f0',
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: 'white'
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 140 }}>
                Дата
              </TableCell>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 100 }}>
                Отправитель
              </TableCell>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem' }}>
                Сообщение
              </TableCell>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 110 }}>
                Важность
              </TableCell>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 140 }}>
                Кому
              </TableCell>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 90, textAlign: 'center' }}>
                Прочт.
              </TableCell>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 70, textAlign: 'center' }}>
                Вкл
              </TableCell>
              <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 70, textAlign: 'right' }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#2563eb' }} />
                </TableCell>
              </TableRow>
            ) : notifications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: '#64748b', fontSize: '0.85rem' }}>
                  Нет уведомлений
                </TableCell>
              </TableRow>
            ) : (
              notifications.map(item => {
                const isImportant = item.status === 'important';
                const createdDate = new Date(item.createdAt).toLocaleString('ru-RU', {
                  day: '2-digit', month: '2-digit', year: '2-digit',
                  hour: '2-digit', minute: '2-digit'
                });

                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      borderBottom: '1px solid #f1f5f9',
                      opacity: item.isActive ? 1 : 0.55
                    }}
                  >
                    <TableCell sx={{ py: 0.8, fontSize: '0.78rem', color: '#334155', whiteSpace: 'nowrap' }}>
                      {createdDate}
                    </TableCell>

                    <TableCell sx={{ py: 0.8, fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>
                      {item.sender}
                    </TableCell>

                    <TableCell sx={{ py: 0.8, fontSize: '0.82rem', color: '#1e293b' }}>
                      <Box sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.35
                      }}>
                        {item.message}
                      </Box>
                      {item.expiresAt && (
                        <Typography variant="caption" sx={{ color: '#d97706', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.2 }}>
                          <ScheduleIcon sx={{ fontSize: 11 }} />
                          До: {new Date(item.expiresAt).toLocaleDateString('ru-RU')}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 0.8 }}>
                      <Chip
                        size="small"
                        label={isImportant ? 'Важное' : 'Обычное'}
                        sx={{
                          height: 18,
                          fontSize: '0.67rem',
                          fontWeight: 600,
                          bgcolor: isImportant ? '#fee2e2' : '#e0f2fe',
                          color: isImportant ? '#b91c1c' : '#0369a1'
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 0.8 }}>
                      {item.targetType === 'all' && (
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                          Всем
                        </Typography>
                      )}
                      {item.targetType === 'pharmacies' && (
                        <Tooltip title={item.targetValues?.join(', ') || ''}>
                          <Chip
                            size="small"
                            label={`Аптек: ${item.targetValues?.length || 0}`}
                            sx={{ height: 18, fontSize: '0.67rem', bgcolor: '#e0e7ff', color: '#3730a3', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      )}
                      {item.targetType === 'roles' && (
                        <Chip
                          size="small"
                          label={item.targetValues?.join(', ')}
                          sx={{ height: 18, fontSize: '0.67rem', bgcolor: '#fef3c7', color: '#92400e' }}
                        />
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => onOpenStats(item.id)}
                        sx={{
                          color: '#2563eb',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          minWidth: 'auto',
                          p: 0.3
                        }}
                      >
                        {item.readCount}
                      </Button>
                    </TableCell>

                    <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                      <Switch
                        size="small"
                        checked={item.isActive}
                        onChange={() => onToggleActive(item.id, item.isActive)}
                        color="primary"
                        sx={{ transform: 'scale(0.8)' }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 0.8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Статистика">
                        <IconButton size="small" onClick={() => onOpenStats(item.id)} sx={{ p: 0.3, color: '#64748b', '&:hover': { color: '#2563eb' } }}>
                          <ViewIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <IconButton size="small" onClick={() => onDelete(item.id)} sx={{ p: 0.3, color: '#64748b', '&:hover': { color: '#ef4444' } }}>
                          <DeleteIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={e => onRowsPerPageChange(parseInt(e.target.value, 10))}
        sx={{
          color: '#64748b',
          borderTop: '1px solid #e2e8f0',
          '& .MuiTablePagination-toolbar': { minHeight: 40, py: 0 },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.8rem' }
        }}
      />
    </Paper>
  );
};

export default NotificationTable;
